// src/pages/property-details/components/ContactForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { createMarketInquiry } from '../../../services/inquiryApi';

const CONTACT_METHOD_OPTIONS = [
  { value: 'EMAIL', label: 'E-posta' },
  { value: 'PHONE', label: 'Telefon' },
  { value: 'TEXT', label: 'Mesaj' },
];

const CONTACT_TIME_OPTIONS = [
  { value: 'ANYTIME', label: 'Fark etmez' },
  { value: 'MORNING', label: 'Sabah (08:00 - 12:00)' },
  { value: 'AFTERNOON', label: 'Ogleden sonra (12:00 - 17:00)' },
  { value: 'EVENING', label: 'Aksam (17:00 - 20:00)' },
];

const buildDefaultMessage = (property) => (
  `Merhaba, "${property?.title || 'bu urun'}" urunuyle ilgileniyorum. `
  + 'Miktar ve teslimat detaylarini paylasabilir misiniz?'
);

const buildInitialFormData = (user, property) => ({
  name: user?.fullName || '',
  email: user?.email || '',
  phone: user?.phone || '',
  message: buildDefaultMessage(property),
  contactMethod: 'EMAIL',
  preferredTime: 'ANYTIME',
});

const ContactForm = ({ property, agent, onClose }) => {
  const { user } = useAuth();
  const { success: toastSuccess, error: toastError } = useToast();
  const [formData, setFormData] = useState(() => buildInitialFormData(user, property));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const normalizedPhone = useMemo(() => `${formData.phone || ''}`.trim(), [formData.phone]);

  useEffect(() => {
    setFormData(buildInitialFormData(user, property));
    setIsSubmitted(false);
    setErrors({});
  }, [property?.id, user?.id]);

  const validateForm = () => {
    const nextErrors = {};

    if (!`${formData.name || ''}`.trim()) {
      nextErrors.name = 'Ad soyad zorunludur.';
    }

    if (!`${formData.email || ''}`.trim()) {
      nextErrors.email = 'E-posta zorunludur.';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      nextErrors.email = 'Gecerli bir e-posta adresi girin.';
    }

    if (normalizedPhone && !/^[0-9+()\-\s.]+$/.test(normalizedPhone)) {
      nextErrors.phone = 'Gecerli bir telefon numarasi girin.';
    }

    if (!`${formData.message || ''}`.trim()) {
      nextErrors.message = 'Mesaj zorunludur.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!property?.id) {
      toastError('Urun bulunamadi.');
      return;
    }

    setIsSubmitting(true);
    try {
      await createMarketInquiry({
        productId: property.id,
        message: formData.message.trim(),
        contactName: formData.name.trim(),
        contactEmail: formData.email.trim(),
        contactPhone: normalizedPhone || undefined,
        preferredContactMethod: formData.contactMethod,
        preferredContactTime: formData.preferredTime,
      });

      setIsSubmitted(true);
      toastSuccess('Mesajin saticiya iletildi.');
      setTimeout(() => {
        onClose?.();
      }, 1600);
    } catch (error) {
      toastError(error?.message || 'Mesaj gonderilemedi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-modal flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-surface rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {isSubmitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Check" size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">
              Mesajin Iletildi
            </h2>
            <p className="text-text-secondary mb-6">
              {agent?.name || 'Satici'} en kisa surede seninle iletisime gececek.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-700 transition-all duration-200"
            >
              Kapat
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-4">
                <Image
                  src={agent?.avatar}
                  alt={agent?.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h2 className="text-xl font-bold text-text-primary">Satıcıya Ulas</h2>
                  <p className="text-text-secondary">{property?.title}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-secondary-100 rounded-md transition-all duration-200"
                aria-label="Formu kapat"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-text-primary mb-2">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 ${
                      errors.name ? 'border-error' : 'border-border focus:border-primary'
                    }`}
                    placeholder="Ad soyad"
                  />
                  {errors.name && <p className="mt-1 text-sm text-error">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                    E-posta *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 ${
                      errors.email ? 'border-error' : 'border-border focus:border-primary'
                    }`}
                    placeholder="ornek@email.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-error">{errors.email}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 ${
                    errors.phone ? 'border-error' : 'border-border focus:border-primary'
                  }`}
                  placeholder="05xx xxx xx xx"
                />
                {errors.phone && <p className="mt-1 text-sm text-error">{errors.phone}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contactMethod" className="block text-sm font-medium text-text-primary mb-2">
                    Tercih Edilen Iletisim
                  </label>
                  <select
                    id="contactMethod"
                    name="contactMethod"
                    value={formData.contactMethod}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    {CONTACT_METHOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-medium text-text-primary mb-2">
                    Uygun Zaman
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    {CONTACT_TIME_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-2">
                  Mesaj *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 resize-vertical ${
                    errors.message ? 'border-error' : 'border-border focus:border-primary'
                  }`}
                  placeholder="Mesajinizi yazin..."
                />
                {errors.message && <p className="mt-1 text-sm text-error">{errors.message}</p>}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      message: `Merhaba, ${property?.title} icin uygun miktar, teslimat ve odeme detaylarini iletebilir misiniz?`,
                    }))
                  }
                  className="px-3 py-1 text-sm bg-secondary-100 text-text-secondary rounded-md hover:bg-secondary-200 transition-all duration-200"
                >
                  Fiyat ve Teslimat
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      message: `Merhaba, ${property?.title} urunu icin duzenli alim yapmak istiyorum. Numune ve surec hakkinda bilgi verebilir misiniz?`,
                    }))
                  }
                  className="px-3 py-1 text-sm bg-secondary-100 text-text-secondary rounded-md hover:bg-secondary-200 transition-all duration-200"
                >
                  Duzenli Alim
                </button>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-text-secondary hover:text-text-primary transition-all duration-200"
                >
                  Iptal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isSubmitting ? 'Gonderiliyor...' : 'Mesaji Gonder'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
